import { useEffect, useRef, useState } from "react";
import { api, useAuth } from "@/hooks/useAuth";
import "./HomeLanding.css";

/**
 * HomeLanding — EXACTLY 9 sections (Apple × Krishna × Future AI).
 * 01 Hero · 02 Just Talk · 03 Life Coach + AI Astrologer · 04 Your Future
 * 05 From Wisdom to Action · 06 Digital Temple · 07 KALKI Gets to Know You
 * 08 One Intelligence. Your Life. · 09 Final CTA + footer.
 */
const KRISHNA = "/assets/kalki-krishna.png";
// Per-section Krishna variants — each of the 5 new images appears as the HERO
// of at least one section; shorter / smaller sections reuse where appropriate.
const K_HERO    = "/assets/krishna/k0-hero-blue.png";      // s1 — divine blue deity, open-hand welcome [HERO]
const K_TALK    = "/assets/krishna/k2-resting.png";        // s2 — intimate, resting on hand       [IMG 2]
const K_COACH   = "/assets/krishna/k5-flute-playing.png";  // s3 — playing flute (wisdom)          [IMG 5]
const K_ACTION  = "/assets/krishna/k4-serene-rock.png";    // s5 — serene stance on the rock       [IMG 4]
const K_TEMPLE  = "/assets/krishna/k3-dancing.png";        // s6 — dancing with open arms          [IMG 3]
const K_KNOW    = "/assets/krishna/k2-resting.png";        // s7 — reflective (reuse)
const K_CONST   = "/assets/krishna/k5-flute-playing.png";  // s8 — centerpiece (reuse)
const K_FINAL   = "/assets/krishna/krishna-medallion.png"; // s9 — radiant namaste medallion
const APP_URL = "/app";

export default function HomeLanding() {
  const { user: authUser } = useAuth();
  const [navScrolled, setNavScrolled] = useState(false);
  // Honour ?signin=1 (from OnboardingGuard redirect) by auto-opening the modal.
  useEffect(() => {
    try {
      const q = new URLSearchParams(window.location.search);
      if (q.get("signin") === "1") {
        setModalMode("signin");
        setModalOpen(true);
        // scrub the param so a reload doesn't re-open the modal
        window.history.replaceState({}, "", window.location.pathname);
      }
    } catch (_) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [menuOpen, setMenuOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  // mode: "signup" | "signin" | "forgot"
  const [modalMode, setModalMode] = useState("signup");
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formPassword, setFormPassword] = useState("");
  const [formConfirm, setFormConfirm] = useState("");
  const [formBusy, setFormBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [formInfo, setFormInfo] = useState("");
  const [sankalpAccepted, setSankalpAccepted] = useState(false);
  const [karmaDone, setKarmaDone] = useState(false);
  const [dharma, setDharma] = useState(72);
  const [reasonOpen, setReasonOpen] = useState(false);
  const [prasadChoice, setPrasadChoice] = useState(null);
  const [prasadThanks, setPrasadThanks] = useState(false);
  const [prasadOpen, setPrasadOpen] = useState(false);
  const [predRevealed, setPredRevealed] = useState(false);
  const [futureTick, setFutureTick] = useState(2);
  const [dharmaIn, setDharmaIn] = useState(false);
  const [blessingOpen, setBlessingOpen] = useState(false);
  const [talkQ, setTalkQ] = useState(0);
  const [bellMsg, setBellMsg] = useState(false);
  const scrollLineRef = useRef(null);
  const dharmaBarsRef = useRef(null);
  const bellWaveRef = useRef(null);
  const predCardRef = useRef(null);

  // Rotate the "Life is full of questions" micro-copy
  useEffect(() => {
    const id = setInterval(() => setTalkQ((q) => (q + 1) % 3), 2400);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    document.body.classList.add("kalki-home");
    document.title = "KALKI — Life Coach & AI Astrologer | superlife.living";
    const onScroll = () => {
      const sc = window.scrollY;
      setNavScrolled(sc > 60);
      const h = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollLineRef.current) scrollLineRef.current.style.width = `${(sc / Math.max(1, h)) * 100}%`;
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("in"); }),
      { threshold: 0.15 });
    document.querySelectorAll(".k-reveal").forEach((el) => io.observe(el));
    const io3 = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setPredRevealed(true); }),
      { threshold: 0.3 });
    if (predCardRef.current) io3.observe(predCardRef.current);
    const io4 = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setDharmaIn(true); }),
      { threshold: 0.3 });
    if (dharmaBarsRef.current) io4.observe(dharmaBarsRef.current);
    return () => {
      window.removeEventListener("scroll", onScroll);
      io.disconnect(); io3.disconnect(); io4.disconnect();
      document.body.classList.remove("kalki-home");
    };
  }, []);

  const ringBell = () => {
    if (!bellWaveRef.current) return;
    bellWaveRef.current.classList.remove("on"); void bellWaveRef.current.offsetWidth;
    bellWaveRef.current.classList.add("on");
    setBellMsg(true);
    setTimeout(() => bellWaveRef.current?.classList.remove("on"), 2300);
    setTimeout(() => setBellMsg(false), 6000);
  };

  const offerPrasad = () => {
    if (!prasadChoice) return;
    setPrasadThanks(true);
    setTimeout(() => { setPrasadThanks(false); setPrasadOpen(false); setPrasadChoice(null); }, 4500);
  };

  const completeKarma = () => {
    if (karmaDone) return;
    setKarmaDone(true);
    setTimeout(() => setDharma((d) => Math.min(100, d + 1)), 700);
  };

  const QUESTIONS = ["Am I on the right path?", "What may lie ahead?", "What should I do next?"];

  return (
    <>
      <div className="k-scroll" ref={scrollLineRef} />

      {/* ============ NAV ============ */}
      <nav className={`k-nav ${navScrolled ? "scrolled" : ""}`} data-testid="k-nav">
        <div className="k-nav-inner">
          <div className="k-mark">KALKI</div>
          <ul className="k-nav-links">
            <li><a href="#s2">Experience</a></li>
            <li><a href="#s3">Astrology</a></li>
            <li><a href="#s7">Journey</a></li>
          </ul>
          <div className="k-nav-right">
            {authUser ? (
              <a
                href={APP_URL}
                className="k-signin"
                data-testid="nav-profile-pill"
                style={{display:"inline-flex",alignItems:"center",gap:8,textDecoration:"none"}}
              >
                {authUser.picture ? (
                  <img
                    src={authUser.picture}
                    alt=""
                    style={{width:28,height:28,borderRadius:"50%",objectFit:"cover",border:"1px solid rgba(255,255,255,0.3)"}}
                  />
                ) : (
                  <span style={{width:28,height:28,borderRadius:"50%",background:"linear-gradient(135deg,#4F46E5,#D946EF)",display:"inline-flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:12,fontWeight:600}}>
                    {(authUser.name || authUser.email || "K").charAt(0).toUpperCase()}
                  </span>
                )}
                <span style={{fontSize:13}} className="mobile-hide">{(authUser.name || authUser.email || "").split(" ")[0]}</span>
              </a>
            ) : (
              <button className="k-signin" data-testid="nav-signin" onClick={() => { setModalMode("signin"); setModalOpen(true); }}>Sign In</button>
            )}
            <a className="k-cta mobile-hide" href={APP_URL} data-testid="nav-cta-talk">TALK NOW</a>
            <button className="k-menu-btn" onClick={() => setMenuOpen(true)} aria-label="Open menu">MENU</button>
          </div>
        </div>
      </nav>

      <div className={`k-mobile-menu ${menuOpen ? "open" : ""}`}>
        <button className="k-mobile-close" onClick={() => setMenuOpen(false)}>CLOSE</button>
        <a href="#s2" onClick={() => setMenuOpen(false)}>Experience</a>
        <a href="#s3" onClick={() => setMenuOpen(false)}>Astrology</a>
        <a href="#s7" onClick={() => setMenuOpen(false)}>Journey</a>
        <a href={APP_URL}>Talk to KALKI →</a>
      </div>

      {/* ============ 01 HERO ============ */}
      <section id="s1" className="k-hero k-grid-bg" data-testid="s1-hero">
        <div className="k-ghost-mark" style={{ bottom: "4%", left: "-2%" }}>KALKI</div>
        <div className="k-wrap">
          <div className="k-hero-copy">
            <div className="k-eyebrow">LIFE COACH · AI ASTROLOGER</div>
            <h1>KALKI</h1>
            <div className="k-h2">KNOW YOUR PATH.<br /><em>LIVE IT BETTER.</em></div>
            <p className="k-lede">Talk about your life.<br />Understand your timing.<br />Know what to do next.</p>
            <div className="k-hero-ctas">
              <a className="k-cta" href={APP_URL} data-testid="hero-cta-talk">🎙 Talk to KALKI</a>
              <a className="k-cta ghost" href="#s2">Explore ↓</a>
            </div>

            {/* Life Report hero pill — small premium highlight below primary CTA */}
            <a href={`${APP_URL}/report`} className="k-lr-pill" data-testid="hero-lr-pill">
              <span className="k-lr-badge">✦ NEW</span>
              <span className="k-lr-copy">
                <strong>LIFE REPORT</strong>
                <small>Your complete Vedic life journey · 500 Credits</small>
              </span>
              <span className="k-lr-arrow">→</span>
            </a>
            <div style={{marginTop:28,fontSize:11,letterSpacing:".3em",color:"var(--mute)",textTransform:"uppercase",fontStyle:"italic"}}>Inspired by the wisdom of Lord Krishna</div>
          </div>
          <div className="k-krishna-wrap">
            <img className="k-krishna" src={K_HERO} alt="KALKI companion portrait — welcoming the user." />
            <button className="k-talk-orb" onClick={() => (window.location.href = APP_URL)} data-testid="hero-palm-orb" aria-label="Talk">
              <span className="live" />
              <span className="mic">🎙</span>
              <span className="lbl">Talk</span>
            </button>
            <span className="k-float-chip" style={{ top: "12%", right: "4%" }}>✨ Ask me anything</span>
          </div>
        </div>
      </section>

      {/* ============ 02 JUST TALK ============ */}
      <section id="s2" className="k-section k-dark k-universe" data-testid="s2-talk">
        <div className="k-stars" />
        <div className="k-wrap k-reveal k-s2-grid">
          <div className="k-s2-left">
            <div className="k-eyebrow" style={{color:"#A1A1AA"}}>The conversation</div>
            <h2 className="k-h2" style={{color:"#F8FAFC"}}>Life is full<br />of questions.</h2>
            <div className="k-s2-rot" style={{marginTop:28,minHeight:40,fontFamily:"var(--serif)",fontSize:"clamp(20px,1.8vw,26px)",color:"#DBB9FF",letterSpacing:"-.01em",transition:"opacity .4s"}}>
              {QUESTIONS.map((q, i) => (
                <span key={i} style={{display: i===talkQ?"block":"none",opacity:1}}>“{q}”</span>
              ))}
            </div>
            <h3 className="k-h3" style={{color:"#F8FAFC",margin:"44px 0 20px"}}>Just talk.</h3>
            <div className="k-glass-dark k-chatbox" style={{color:"#F8FAFC",maxWidth:520}}>
              <div className="k-chat-row you">
                <div className="bubble" style={{background:"rgba(255,46,154,.1)",color:"#F8FAFC",borderColor:"rgba(255,46,154,.25)"}}>I'm building something I believe in, but I'm afraid it may fail.</div>
              </div>
              <div className="k-chat-row krishna">
                <div className="bubble">Then let us separate fear from the decision. Why does this path matter to you?</div>
              </div>
              <div className="k-chat-ctrls" style={{borderTopColor:"rgba(255,255,255,.08)"}}>
                <button data-testid="demo-talk" style={{background:"rgba(255,255,255,.06)",color:"#F8FAFC",border:"1px solid rgba(255,255,255,.14)"}}>🎙 Talk</button>
                <button data-testid="demo-chat" style={{background:"rgba(255,255,255,.06)",color:"#F8FAFC",border:"1px solid rgba(255,255,255,.14)"}}>💬 Chat</button>
                <div style={{marginLeft:"auto",fontSize:11,color:"#A1A1AA",letterSpacing:".1em"}}>No searching · No forms</div>
              </div>
            </div>
          </div>
          <div className="k-s2-right">
            <div className="k-krishna-wrap k-s2-krishna">
              <img className="k-krishna" src={K_TALK} alt="KALKI companion portrait" />
            </div>
          </div>
        </div>
      </section>

      {/* ============ 03 LIFE COACH + AI ASTROLOGER ============ */}
      <section id="s3" className="k-section k-grid-bg" data-testid="s3-split">
        <div className="k-wrap k-reveal" style={{textAlign:"center"}}>
          <div className="k-eyebrow" style={{justifyContent:"center",display:"inline-flex"}}>KALKI</div>
          <h2 className="k-h2">Two ways<br />to see your life.</h2>
        </div>
        <div className="k-wrap" style={{marginTop:60}}>
          <div className="k-split" style={{display:"grid",gridTemplateColumns:"1fr auto 1fr",alignItems:"center",gap:40}}>
            <div className="k-reveal" data-testid="split-coach">
              <div className="k-eyebrow">Life Coach</div>
              <h3 className="k-h2" style={{fontSize:"clamp(32px,4.5vw,64px)"}}>Understand<br />yourself.</h3>
              <p className="k-lede" style={{maxWidth:"34ch"}}>KALKI learns your goals, choices, patterns and important life moments to help you see your situation more clearly.</p>
              <div style={{marginTop:28,display:"flex",flexWrap:"wrap",gap:8}}>
                {["Character","Decisions","Karma","Sankalp","Journey"].map((t) => (
                  <span key={t} className="k-glass" style={{padding:"6px 14px",borderRadius:999,fontSize:11,letterSpacing:".15em",textTransform:"uppercase"}}>{t}</span>
                ))}
              </div>
            </div>
            <div className="k-krishna-wrap k-split-krishna" style={{height:"58svh",width:280}}>
              <img className="k-krishna" src={K_COACH} alt="" style={{height:"100%"}} />
            </div>
            <div className="k-reveal" data-testid="split-astro" style={{textAlign:"right"}}>
              <div className="k-eyebrow" style={{justifyContent:"flex-end",display:"inline-flex"}}>AI Astrologer</div>
              <h3 className="k-h2" style={{fontSize:"clamp(32px,4.5vw,64px)"}}>Understand<br />your timing.</h3>
              <p className="k-lede" style={{maxWidth:"34ch",marginLeft:"auto"}}>KALKI uses Vedic astrology to explore important periods, patterns and possibilities surrounding your life.</p>
              <div style={{marginTop:28,display:"flex",flexWrap:"wrap",gap:8,justifyContent:"flex-end"}}>
                {["Career","Money","Love","Family","Purpose"].map((t) => (
                  <span key={t} className="k-glass" style={{padding:"6px 14px",borderRadius:999,fontSize:11,letterSpacing:".15em",textTransform:"uppercase"}}>{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
        <p className="k-reveal" style={{textAlign:"center",marginTop:80,fontFamily:"var(--serif)",fontSize:"clamp(28px,3.4vw,48px)",fontWeight:300,color:"var(--ink)"}}>
          One life. One conversation.
        </p>
      </section>

      {/* ============ 04 YOUR FUTURE ============ */}
      <section id="s4" className="k-section k-dark k-universe" data-testid="s4-future">
        <div className="k-stars" />
        <div className="k-wrap k-reveal" style={{textAlign:"center",position:"relative",zIndex:3}}>
          <div className="k-eyebrow" style={{justifyContent:"center",display:"inline-flex",color:"#A1A1AA"}}>Your future</div>
          <h2 className="k-h2" style={{color:"#F8FAFC"}}>What may<br />lie ahead?</h2>
          <p className="k-lede" style={{color:"#C7C7D1",margin:"28px auto 0",textAlign:"center"}}>KALKI looks beyond the present to help you understand important periods that may be forming.</p>
          <div className="k-future-rail" style={{marginTop:60}}>
            <div className="k-future-line" />
            {[
              { t:"Now", x:"4%", m:"Clarify one meaningful move." },
              { t:"3 Months", x:"24%", m:"Career: steady re-alignment." },
              { t:"1 Year", x:"44%", m:"Purpose crystallises around service." },
              { t:"3 Years", x:"68%", m:"Expansion window — career & purpose." },
              { t:"5 Years", x:"92%", m:"Deep anchoring. Steady leadership." },
            ].map((p, i) => (
              <button key={p.t} className={`k-future-tick ${futureTick === i ? "active" : ""}`} style={{left:p.x}}
                      onMouseEnter={() => setFutureTick(i)} onFocus={() => setFutureTick(i)} data-testid={`future-tick-${i}`}>
                <span className="tag" style={{color:"#F8FAFC"}}>{p.t}</span>
                <span className="moment">{p.m}</span>
              </button>
            ))}
          </div>

          <div ref={predCardRef} className={`k-glass-dark k-pred-card ${predRevealed ? "reveal" : ""}`} data-testid="prediction-card" style={{marginTop:80}}>
            <div className="k-pred-eyebrow">✦ KALKI insight</div>
            <h3>A period of<br />expansion.</h3>
            <div className="k-pred-meta">
              <span>Career & Purpose · 2028–2029</span>
              <span className="pill">● HIGH ALIGNMENT</span>
            </div>
            <p style={{color:"#C7C7D1",marginTop:20,fontSize:14,lineHeight:1.5}}>Your Vedic timing suggests a potentially important period for professional growth.</p>
            <div className="k-pred-actions">
              <button onClick={() => setReasonOpen((o) => !o)} data-testid="pred-why">Why do you see this?</button>
              <a href={APP_URL} className="k-cta lime" style={{padding:"12px 20px",textDecoration:"none"}}>What should I do?</a>
            </div>
            <div className={`k-reason ${reasonOpen ? "open" : ""}`} data-testid="pred-reasoning" style={{marginTop:24}}>
              <div style={{fontSize:11,letterSpacing:".3em",textTransform:"uppercase",color:"#A1A1AA",marginBottom:16}}>Why I see this</div>
              {["Vedic timing","Your past","Your present","Your patterns","Gita-inspired wisdom"].map((h, i) => (
                <div key={h} className="k-reason-step"><div className="num">{String(i+1).padStart(2,"0")}</div><div><h4 style={{color:"#F8FAFC",margin:0}}>{h}</h4></div></div>
              ))}
              <div style={{marginTop:16,fontSize:12,letterSpacing:".2em",color:"var(--lime)",textTransform:"uppercase"}}>Your next karma →</div>
            </div>
          </div>

          <p style={{marginTop:80,fontFamily:"var(--serif)",fontSize:"clamp(28px,3.4vw,48px)",fontWeight:300,color:"#F8FAFC",lineHeight:1.15}}>
            Don't just see<br/>what may happen.<br/>Understand what to do with it.
          </p>

          {/* ===== LIFE REPORT showcase (inside Section 04) ===== */}
          <div className="k-lr-showcase" data-testid="lr-showcase">
            <div className="k-lr-eyebrow">✦ KALKI Life Report</div>
            <h2 className="k-h2" style={{color:"#F8FAFC",marginTop:12}}>Your life.<br/><em style={{fontStyle:"normal",background:"linear-gradient(135deg,var(--indigo),var(--fuchsia))",WebkitBackgroundClip:"text",color:"transparent"}}>Seen together.</em></h2>
            <p className="k-lede" style={{color:"#C7C7D1",maxWidth:"52ch",margin:"24px auto 0",textAlign:"center"}}>
              A complete Vedic Life Report built around your birth chart, life journey, important questions and what may lie ahead.
            </p>

            <div className="k-lr-mockup-wrap">
              <div className="k-lr-mockup-back lr-b1" />
              <div className="k-lr-mockup-back lr-b2" />
              <div className="k-lr-mockup">
                <div className="lr-mark">KALKI</div>
                <div className="lr-title">Your<br/>Life Report.</div>
                <div className="lr-name">KARNA DESAI</div>
                <div className="lr-sub">A Vedic Life Journey</div>
                <div className="lr-quote">“Understand your path.<br/>Shape your karma.”</div>
                <div className="lr-foot">500 Credits · Oct 2026</div>
              </div>
              <span className="k-lr-chip lr-c1">Your Character</span>
              <span className="k-lr-chip lr-c2">Career & Purpose</span>
              <span className="k-lr-chip lr-c3">Love & Relationships</span>
              <span className="k-lr-chip lr-c4">Money & Growth</span>
              <span className="k-lr-chip lr-c5">What may lie ahead</span>
              <span className="k-lr-chip lr-c6">Your Karma</span>
            </div>

            {/* Price + CTA */}
            <div className="k-lr-price-row">
              <div className="k-lr-price">
                <div className="lbl">Full Life Report</div>
                <div className="amt">✦ 500 <em>KALKI Credits</em></div>
              </div>
              <div className="k-lr-cta-group">
                <a href={`${APP_URL}/report`} className="k-cta" data-testid="lr-generate-cta">Generate my Life Report</a>
                <a href="#lr-inside" className="k-lr-see">See what's inside →</a>
              </div>
            </div>
            <p className="k-lr-note">Your report is generated using KALKI's Vedic Intelligence and your personal journey.</p>

            {/* Journey flow */}
            <div className="k-lr-flow">
              {["Your Beginning","Your Journey","Your Questions","Vedic Intelligence","Your Life Report"].map((s, i, a) => (
                <span key={s} className="k-lr-flow-step">
                  <span className="dot" />
                  <span className="t">{s}</span>
                  {i < a.length - 1 && <span className="sep">→</span>}
                </span>
              ))}
            </div>
            <h3 className="k-h3" style={{color:"#F8FAFC",textAlign:"center",marginTop:40,fontWeight:300,fontSize:"clamp(26px,3vw,40px)"}}>
              No long forms.<br/>KALKI guides you through a <em style={{fontStyle:"normal",color:"var(--lime)"}}>6-step personal journey.</em>
            </h3>
            <p style={{textAlign:"center",color:"#A1A1AA",marginTop:14,fontSize:13,maxWidth:"48ch",marginLeft:"auto",marginRight:"auto",lineHeight:1.5}}>
              KALKI remembers what you have already shared and only asks for what is still needed.
            </p>

            {/* Inside list */}
            <div id="lr-inside" className="k-lr-inside">
              {["Your Inner Blueprint","Your Journey So Far","Career & Purpose","Money & Growth","Love & Relationships","Family & Home","Spiritual Journey","What May Lie Ahead","Your Questions","Your Karma","Remedies","Your Sankalp"].map((it) => (
                <span key={it} className="k-lr-inside-item">{it}</span>
              ))}
            </div>
            <h3 style={{color:"#F8FAFC",textAlign:"center",marginTop:48,fontFamily:"var(--serif)",fontWeight:300,fontSize:"clamp(36px,5vw,72px)",lineHeight:1.05,letterSpacing:"-.01em"}}>
              One journey.<br/><em style={{fontStyle:"normal",background:"linear-gradient(135deg,var(--fuchsia),var(--lime))",WebkitBackgroundClip:"text",color:"transparent"}}>One report.</em>
            </h3>
          </div>
        </div>
      </section>

      {/* ============ 05 FROM WISDOM TO ACTION ============ */}
      <section id="s5" className="k-section k-grid-bg" data-testid="s5-action">
        <div className="k-wrap k-reveal">
          <div style={{textAlign:"center",marginBottom:60}}>
            <div className="k-eyebrow" style={{justifyContent:"center",display:"inline-flex"}}>Wisdom to action</div>
            <h2 className="k-h2">Wisdom is useful<br />when you live it.</h2>
          </div>
          <div className="k-row" style={{alignItems:"stretch",gap:60}}>
            <div className="k-krishna-wrap" style={{height:"66svh"}}>
              <img className="k-krishna" src={K_ACTION} alt="" style={{height:"100%"}} />
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:20}}>
              {/* STEP 01 Sankalp */}
              <div className="k-glass" style={{padding:24,borderRadius:24}} data-testid="step-sankalp">
                <div style={{fontSize:10,letterSpacing:".3em",textTransform:"uppercase",color:"var(--mute)"}}>01 · My Sankalp</div>
                <h4 style={{fontFamily:"var(--serif)",fontSize:22,fontWeight:400,margin:"8px 0 14px",lineHeight:1.3}}>I will give my complete attention to one meaningful path.</h4>
                <button onClick={() => setSankalpAccepted(true)} data-testid="sankalp-accept"
                        style={{padding:"10px 20px",borderRadius:999,fontSize:13,letterSpacing:".1em",
                                background: sankalpAccepted ? "var(--lime)" : "var(--graphite)",
                                color: sankalpAccepted ? "var(--graphite)" : "#fff"}}>
                  {sankalpAccepted ? "✓ Sankalp Accepted" : "I accept"}
                </button>
              </div>

              {/* STEP 02 Karma */}
              <div className={`k-glass ${karmaDone ? "k-karma-done" : ""}`} style={{padding:24,borderRadius:24,
                background: karmaDone ? "linear-gradient(135deg,rgba(207,255,4,.14),rgba(207,255,4,.03))" : undefined}}
                   data-testid="step-karma">
                <div style={{fontSize:10,letterSpacing:".3em",textTransform:"uppercase",color:"var(--mute)"}}>02 · My Karma · Today</div>
                <h4 style={{fontFamily:"var(--serif)",fontSize:22,fontWeight:400,margin:"8px 0 14px",lineHeight:1.3}}>Have the conversation you've been postponing.</h4>
                <button onClick={completeKarma} data-testid="karma-complete"
                        style={{padding:"10px 20px",borderRadius:999,fontSize:13,letterSpacing:".1em",
                                background: karmaDone ? "var(--lime)" : "transparent",
                                border: karmaDone ? "none" : "1px solid rgba(0,0,0,.14)",
                                color: karmaDone ? "var(--graphite)" : "var(--ink)"}}>
                  {karmaDone ? "✓ Completed" : "Complete"}
                </button>
              </div>

              {/* STEP 03 Dharma */}
              <div className="k-glass" style={{padding:28,borderRadius:24,textAlign:"center"}} data-testid="step-dharma">
                <div style={{fontSize:10,letterSpacing:".3em",textTransform:"uppercase",color:"var(--mute)"}}>03 · Dharma</div>
                <div style={{display:"flex",alignItems:"baseline",justifyContent:"center",gap:12,marginTop:8}}>
                  <span style={{fontFamily:"var(--serif)",fontSize:64,fontWeight:300,lineHeight:1,color:"var(--ink)"}}>{dharma}</span>
                  <span style={{fontSize:11,letterSpacing:".3em",color:"var(--lime)",textTransform:"uppercase"}}>Aligning ↑</span>
                </div>
                <div ref={dharmaBarsRef} style={{display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:10,marginTop:20}}>
                  {[{n:"Karma",v:84},{n:"Sankalp",v:78},{n:"Reflection",v:65},{n:"Consistency",v:58},{n:"Service",v:81}].map((b) => (
                    <div key={b.n}>
                      <div style={{fontSize:9,letterSpacing:".15em",color:"var(--mute)",textTransform:"uppercase",marginBottom:4}}>{b.n}</div>
                      <div style={{height:3,background:"rgba(0,0,0,.06)",borderRadius:999,overflow:"hidden"}}>
                        <div style={{height:"100%",background:"linear-gradient(90deg,var(--indigo),var(--fuchsia))",width:dharmaIn?`${b.v}%`:0,transition:"width 1.4s ease"}} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <p style={{textAlign:"center",marginTop:48,color:"var(--mute)",fontSize:14,maxWidth:"42ch",marginLeft:"auto",marginRight:"auto",lineHeight:1.6}}>Your Dharma grows when insight becomes meaningful action.</p>
          <p style={{textAlign:"center",marginTop:16,fontSize:11,letterSpacing:".3em",color:"var(--indigo)",textTransform:"uppercase",fontWeight:600}}>ACTION &gt; ATTENTION</p>
        </div>
      </section>

      {/* ============ 06 DIGITAL TEMPLE ============ */}
      <section id="s6" className="k-temple k-dark k-universe" data-testid="s6-temple">
        <div className="k-stars" />
        <div className="k-wrap" style={{position:"relative",zIndex:3}}>
          <div style={{textAlign:"center"}}>
            <div className="k-eyebrow" style={{justifyContent:"center",display:"inline-flex",color:"#A1A1AA"}}>Digital temple</div>
            <h2 className="k-h2" style={{color:"#F8FAFC"}}>A temple inside<br />your universe.</h2>
          </div>
          <div className="k-temple-stage">
            <div className="k-temple-center"><img src={K_TEMPLE} alt="" /></div>
            <button className="k-temple-orb tl" onClick={ringBell} data-testid="temple-bell"
                    aria-label="Temple Bell — Remember what matters">
              <div className="ic">🔔</div><div className="nm">Temple Bell</div>
            </button>
            <button className="k-temple-orb tr" onClick={() => setBlessingOpen(true)} data-testid="temple-blessing"
                    aria-label="Blessings — A moment for you">
              <div className="ic">✨</div><div className="nm">Blessings</div>
            </button>
            <button className="k-temple-orb bl" onClick={() => document.getElementById("s5")?.scrollIntoView({behavior:"smooth"})}
                    data-testid="temple-sankalp" aria-label="Sankalp — Your commitment">
              <div className="ic">🙏</div><div className="nm">Sankalp</div>
            </button>
            <button className="k-temple-orb br" onClick={() => setPrasadOpen(true)} data-testid="temple-prasad"
                    aria-label="Prasad — Offer gratitude">
              <div className="ic">🪷</div><div className="nm">Prasad</div>
            </button>
          </div>
          {bellMsg && (
            <div className="k-glass-dark" style={{maxWidth:460,margin:"40px auto 0",padding:24,borderRadius:20,textAlign:"center",color:"#F8FAFC"}} data-testid="bell-message">
              <div style={{fontSize:11,letterSpacing:".3em",color:"var(--lime)",textTransform:"uppercase"}}>🔔 A bell from KALKI</div>
              <div style={{marginTop:12,fontFamily:"var(--serif)",fontSize:20,fontWeight:300}}>Remember the Sankalp you made yesterday?</div>
            </div>
          )}
        </div>
      </section>
      <div ref={bellWaveRef} className="k-bell-wave" />

      {/* ============ 07 KALKI GETS TO KNOW YOU ============ */}
      <section id="s7" className="k-section k-grid-bg" data-testid="s7-know">
        <div className="k-wrap k-reveal">
          <div style={{textAlign:"center",marginBottom:60}}>
            <div className="k-eyebrow" style={{justifyContent:"center",display:"inline-flex"}}>Personal</div>
            <h2 className="k-h2">No forms.<br /><em style={{fontStyle:"normal",background:"linear-gradient(135deg,var(--indigo),var(--fuchsia))",WebkitBackgroundClip:"text",color:"transparent"}}>Just a conversation.</em></h2>
          </div>
          <div className="k-row" style={{alignItems:"center"}}>
            <div className="k-intake-left">
              <div className="k-intake-bubble">Before I look deeper into your journey, when were you born?</div>
              <div className="k-intake-answer">27 December 1988</div>
              <div className="k-intake-bubble">Do you know what time?</div>
              <div className="k-intake-answer">2:53 PM</div>
              <div className="k-intake-bubble">And where did your journey begin?</div>
              <div className="k-intake-answer">Ahmedabad, India</div>
              <div className="k-intake-bubble">Now tell me about one moment that changed your life.</div>
            </div>
            <div className="k-krishna-wrap" style={{height:"56svh"}}>
              <img className="k-krishna" src={K_KNOW} alt="" style={{height:"100%"}} />
            </div>
          </div>

          {/* journey nodes */}
          <div style={{marginTop:60,display:"flex",flexWrap:"wrap",justifyContent:"center",gap:12,maxWidth:720,margin:"60px auto 0"}}>
            {["Birth","Career","Relationships","Goals","Decisions","Life Events","Sankalp","Karma"].map((l, i) => (
              <span key={l} className="k-glass" style={{padding:"8px 16px",borderRadius:999,fontSize:11,letterSpacing:".15em",textTransform:"uppercase",color:"var(--ink)",opacity:.85,animationDelay:`${i*.1}s`}}>{l}</span>
            ))}
          </div>
          <p style={{textAlign:"center",marginTop:16,fontSize:11,letterSpacing:".3em",color:"var(--mute)",textTransform:"uppercase"}}>Your journey</p>

          {/* returning user */}
          <div className="k-glass" style={{maxWidth:620,margin:"72px auto 0",padding:32,borderRadius:28}} data-testid="returning">
            <div className="k-chat-row krishna">
              <div className="bubble">Welcome back. Last time we spoke, you were uncertain about your new venture. Has anything changed?</div>
            </div>
            <div style={{display:"flex",flexWrap:"wrap",gap:10,marginTop:16}}>
              <button style={{padding:"10px 16px",borderRadius:999,background:"#fff",border:"1px solid rgba(0,0,0,.1)",fontSize:13}}>Yes, something happened</button>
              <button style={{padding:"10px 16px",borderRadius:999,background:"#fff",border:"1px solid rgba(0,0,0,.1)",fontSize:13}}>Not yet</button>
              <button style={{padding:"10px 16px",borderRadius:999,background:"#fff",border:"1px solid rgba(0,0,0,.1)",fontSize:13}}>Let's talk</button>
            </div>
          </div>
          <p style={{textAlign:"center",marginTop:28,fontSize:11,letterSpacing:".3em",color:"var(--mute)",textTransform:"uppercase"}}>Not another reading · A continuing journey</p>
        </div>
      </section>

      {/* ============ 08 ONE INTELLIGENCE. YOUR LIFE. ============ */}
      <section id="s8" className="k-section k-dark k-universe" data-testid="s8-intel">
        <div className="k-stars" />
        <div className="k-wrap k-reveal" style={{position:"relative",zIndex:3,textAlign:"center"}}>
          <div className="k-eyebrow" style={{justifyContent:"center",display:"inline-flex",color:"#A1A1AA"}}>One intelligence</div>
          <h2 className="k-h2" style={{color:"#F8FAFC"}}>More than<br />an AI Astrologer.<br/><span style={{opacity:.55}}>More than a Life Coach.</span></h2>
          <div className="k-constellation" style={{marginTop:40}}>
            <div className="k-const-center"><img src={K_CONST} alt="" style={{filter:"drop-shadow(0 20px 40px rgba(255,46,154,.35))"}} /></div>
            {[
              { c:"n1", t:"Character", s:"Who am I?" },
              { c:"n2", t:"Vedic", s:"What's unfolding?" },
              { c:"n3", t:"Wisdom", s:"How to see it?" },
              { c:"n4", t:"Memory", s:"Where have I been?" },
              { c:"n5", t:"Future", s:"What may lie ahead?" },
              { c:"n6", t:"Karma", s:"What should I do?" },
            ].map((n) => (
              <div key={n.c} className={`k-const-node ${n.c}`} style={{background:"rgba(20,20,28,.55)",border:"1px solid rgba(255,255,255,.14)"}} data-testid={`node-${n.c}`}>
                <div className="ttl" style={{color:"#DBB9FF"}}>{n.t}</div>
                <div className="sub" style={{color:"#F8FAFC"}}>{n.s}</div>
              </div>
            ))}
          </div>
          <p style={{marginTop:40,fontSize:12,letterSpacing:".3em",color:"var(--lime)",textTransform:"uppercase"}}>One conversation</p>

          {/* manifesto */}
          <div style={{marginTop:120}}>
            <h3 className="k-h2" style={{color:"#F8FAFC"}}>You cannot<br/>control every outcome.</h3>
            <h3 className="k-h2" style={{color:"#F8FAFC",marginTop:60,opacity:.9}}>But you can<br/>understand your <em style={{fontStyle:"normal",background:"linear-gradient(135deg,var(--indigo),var(--fuchsia))",WebkitBackgroundClip:"text",color:"transparent"}}>timing.</em></h3>
            <h3 className="k-h2" style={{color:"#F8FAFC",marginTop:60}}>And choose your <em style={{fontStyle:"normal",background:"linear-gradient(135deg,var(--fuchsia),var(--lime))",WebkitBackgroundClip:"text",color:"transparent"}}>karma.</em></h3>
            <h3 className="k-h2" style={{color:"#F8FAFC",marginTop:60}}>That can change<br/>the <em style={{fontStyle:"normal",color:"var(--lime)"}}>journey.</em></h3>
          </div>
        </div>
      </section>

      {/* ============ 09 FINAL CTA + FOOTER ============ */}
      <section id="s9" className="k-final k-grid-bg" style={{background:"linear-gradient(180deg,#08080D 0%,#1C1C1C 50%,#FAFAFC 100%)"}} data-testid="s9-final">
        <div className="k-ghost-mark" style={{bottom:"6%",right:"-4%",color:"#fff",opacity:.04}}>KALKI</div>
        <div className="k-wrap" style={{position:"relative",zIndex:3}}>
          <div className="k-final-copy" style={{color:"#F8FAFC",textAlign:"center",margin:"0 auto",maxWidth:720,display:"flex",flexDirection:"column",alignItems:"center"}}>
            <div style={{fontSize:11,letterSpacing:".3em",color:"#DBB9FF",textTransform:"uppercase",marginBottom:12}}>Life Coach · AI Astrologer</div>
            <h1>KALKI</h1>
            <div className="k-h2" style={{marginTop:24}}>Know your path.<br /><em style={{fontStyle:"normal",background:"linear-gradient(135deg,var(--fuchsia),var(--indigo))",WebkitBackgroundClip:"text",color:"transparent"}}>Live it better.</em></div>
            <p>Talk about your life. Explore what may lie ahead. Decide what to do next.</p>
            <div className="k-final-ctas">
              <a href={APP_URL} className="k-final-cta primary" data-testid="final-cta-talk">🎙 Talk to KALKI</a>
              <button className="k-final-cta secondary" onClick={() => { setModalMode("signup"); setModalOpen(true); }} data-testid="final-cta-create">Create account →</button>
            </div>
            <p style={{marginTop:28,fontSize:13,color:"#A1A1AA",fontStyle:"italic"}}>Perhaps clarity begins with a conversation.</p>

            {/* OR GO DEEPER — Life Report secondary premium CTA */}
            <div className="k-final-lr" data-testid="final-lr">
              <div className="k-final-lr-eyebrow">Or go deeper</div>
              <div className="k-final-lr-title">✦ Life Report</div>
              <p className="k-final-lr-sub">Your complete Vedic life journey.<br/>500 KALKI Credits</p>
              <a href={`${APP_URL}/report`} className="k-cta" data-testid="final-lr-cta" style={{marginTop:12}}>Generate Life Report →</a>
            </div>

            {/* Credits minimal */}
            <div className="k-glass-dark" style={{marginTop:40,maxWidth:360,width:"100%",padding:20,borderRadius:20,textAlign:"center"}} data-testid="credits-mini">
              <div style={{fontSize:10,letterSpacing:".3em",textTransform:"uppercase",color:"#A1A1AA"}}>✦ KALKI Credits</div>
              <div style={{fontSize:12,color:"#C7C7D1",marginTop:8}}>Credits power AI conversations and voice experiences.</div>
              <a href={APP_URL} style={{display:"inline-block",marginTop:12,padding:"8px 18px",borderRadius:999,background:"var(--graphite)",color:"#fff",fontSize:12,textDecoration:"none",letterSpacing:".1em"}}>Recharge</a>
            </div>
          </div>
        </div>

        {/* integrated footer */}
        <div style={{position:"relative",zIndex:3,marginTop:80,paddingTop:40,borderTop:"1px solid rgba(0,0,0,.08)",maxWidth:1280,margin:"80px auto 0",padding:"40px 24px 20px"}}>
          <div style={{display:"flex",flexWrap:"wrap",justifyContent:"space-between",gap:28,alignItems:"flex-start"}}>
            <div>
              <div className="k-mark" style={{color:"var(--ink)"}}>KALKI</div>
              <div style={{fontSize:11,letterSpacing:".2em",color:"var(--mute)",marginTop:4,textTransform:"uppercase"}}>by superlife.living</div>
            </div>
            <ul style={{display:"flex",gap:24,listStyle:"none",fontSize:12,color:"var(--mute)"}}>
              <li><a href="#s2">Experience</a></li>
              <li><a href="#s3">AI Astrologer</a></li>
              <li><a href="#privacy">Privacy</a></li>
              <li><a href="#terms">Terms</a></li>
              <li><a href="mailto:hello@superlife.living">Contact</a></li>
            </ul>
          </div>
          <p style={{marginTop:28,fontSize:11,color:"var(--mute)",lineHeight:1.6,maxWidth:720}}>
            KALKI is an AI life-coaching and spiritual experience inspired by Lord Krishna, the Bhagavad Gita and Vedic traditions. Astrological insights reflect traditional interpretations, patterns and possibilities rather than guaranteed outcomes.
          </p>
          <p style={{marginTop:14,fontSize:10,letterSpacing:".2em",color:"var(--mute)",textTransform:"uppercase"}}>© 2026 superlife.living</p>
        </div>
      </section>

      {/* ============ PRASAD GLASS SHEET ============ */}
      <div className={`k-bless-modal ${prasadOpen ? "open" : ""}`} onClick={() => !prasadThanks && setPrasadOpen(false)}>
        <div className="k-glass-dark k-bless-card" style={{maxWidth:620}} onClick={(e) => e.stopPropagation()} data-testid="prasad-sheet">
          {!prasadThanks ? (
            <>
              <div style={{textAlign:"center",marginBottom:28}}>
                <div style={{fontSize:11,letterSpacing:".3em",color:"var(--fuchsia)",textTransform:"uppercase"}}>Prasad</div>
                <h3 style={{fontFamily:"var(--serif)",fontSize:28,fontWeight:300,color:"#F8FAFC",marginTop:12}}>Offer gratitude.</h3>
                <p style={{color:"#C7C7D1",fontSize:14,marginTop:8}}>When something you discussed with KALKI begins to unfold, return with gratitude.</p>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:28}}>
                {[
                  { k:"makhan", l:"Makhan", c:"#FFFDF6", a:"#FFE9B4" },
                  { k:"mishri", l:"Mishri", c:"#F8FAFF", a:"#C7D2FE" },
                  { k:"laddu",  l:"Laddu",  c:"#F0B869", a:"#8C6B29" },
                  { k:"kheer",  l:"Kheer",  c:"#F3EFE4", a:"#DFB96E" },
                  { k:"coconut",l:"Coconut",c:"#E6D8B6", a:"#8C6B29" },
                  { k:"banana", l:"Banana", c:"#F3E4A9", a:"#C7A24B" },
                  { k:"fruit",  l:"Fruit",  c:"#F3C27C", a:"#D97706" },
                  { k:"tulsi",  l:"Tulsi",  c:"#B6DBB2", a:"#5BAD7E" },
                  { k:"dry",    l:"Dry Fruits", c:"#F0D18A", a:"#8C6B29" },
                ].map((p) => (
                  <button key={p.k} onClick={() => setPrasadChoice(p.k)} data-testid={`prasad-${p.k}`}
                          style={{aspectRatio:1,borderRadius:999,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
                                  background:"rgba(255,255,255,.06)",
                                  border: prasadChoice === p.k ? "2px solid var(--indigo)" : "1px solid rgba(255,255,255,.14)",
                                  color:"#F8FAFC",transition:"all .25s"}}>
                    <svg viewBox="0 0 48 48" style={{width:"50%"}}>
                      <defs><radialGradient id={`pg-${p.k}`} cx="35%" cy="30%">
                        <stop offset="0%" stopColor="#fff" />
                        <stop offset="60%" stopColor={p.c} />
                        <stop offset="100%" stopColor={p.a} />
                      </radialGradient></defs>
                      <circle cx="24" cy="26" r="14" fill={`url(#pg-${p.k})`} />
                    </svg>
                    <span style={{fontSize:9,letterSpacing:".15em",marginTop:4,color:"#C7C7D1",textTransform:"uppercase"}}>{p.l}</span>
                  </button>
                ))}
              </div>
              <button onClick={offerPrasad} disabled={!prasadChoice} data-testid="prasad-offer-btn"
                      style={{display:"block",width:"100%",padding:"14px",borderRadius:999,
                              background: prasadChoice ? "linear-gradient(135deg,var(--indigo),var(--fuchsia))" : "rgba(255,255,255,.08)",
                              color:"#fff",fontSize:14,letterSpacing:".1em",opacity: prasadChoice ? 1 : .5}}>
                Offer Prasad
              </button>
            </>
          ) : (
            <div style={{textAlign:"center"}} data-testid="prasad-thanks">
              <div style={{fontSize:40}}>🙏</div>
              <h3 style={{fontFamily:"var(--serif)",fontSize:26,fontWeight:300,color:"#F8FAFC",marginTop:16}}>Your gratitude becomes part of your journey.</h3>
              <div style={{marginTop:20,fontSize:11,letterSpacing:".3em",color:"var(--mute)",textTransform:"uppercase"}}>What happened?</div>
              <a href={APP_URL} className="k-cta lime" style={{marginTop:16,display:"inline-block",padding:"12px 24px",textDecoration:"none"}}>Tell KALKI</a>
            </div>
          )}
        </div>
      </div>

      {/* ============ BLESSING MODAL ============ */}
      <div className={`k-bless-modal ${blessingOpen ? "open" : ""}`} onClick={() => setBlessingOpen(false)}>
        <div className="k-glass-dark k-bless-card" onClick={(e) => e.stopPropagation()} data-testid="blessing-modal">
          <div className="ic">✨</div>
          <div className="eye">A blessing for you</div>
          <div className="msg">"May the courage you found today remain with you when the road becomes uncertain."</div>
          <div className="earn">Earned through meaningful action</div>
          <button onClick={() => setBlessingOpen(false)} style={{marginTop:28,fontSize:12,letterSpacing:".2em",color:"#A1A1AA"}}>CLOSE</button>
        </div>
      </div>

      {/* ============ ACCOUNT MODAL ============ */}
      <div className={`k-modal ${modalOpen ? "open" : ""}`} onClick={() => { setModalOpen(false); setFormError(""); setFormInfo(""); }}>
        <div className="k-glass-dark k-modal-card" onClick={(e) => e.stopPropagation()} data-testid="account-modal">
          <button className="k-modal-close" onClick={() => { setModalOpen(false); setFormError(""); setFormInfo(""); }}>CLOSE</button>
          <div className="k-mark">KALKI</div>
          <div className="sub">
            {modalMode === "signin" ? "Welcome back." :
             modalMode === "forgot" ? "We'll help you back in." : "Begin your journey."}
          </div>

          {modalMode !== "forgot" && (
            <>
              <a href="/app" onClick={(e) => {
                e.preventDefault();
                const redirectUrl = window.location.origin + "/app";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }} className="g-btn" data-testid="modal-google">
                <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M23 12.25c0-.8-.07-1.56-.21-2.3H12v4.37h6.17c-.27 1.42-1.06 2.62-2.26 3.42v2.84h3.65c2.13-1.96 3.44-4.85 3.44-8.33z"/><path fill="#34A853" d="M12 23c3.06 0 5.63-1 7.5-2.72l-3.65-2.84c-1.01.68-2.3 1.08-3.85 1.08-2.96 0-5.47-2-6.37-4.7H1.86v2.95C3.72 20.4 7.56 23 12 23z"/><path fill="#FBBC05" d="M5.63 13.82c-.23-.68-.37-1.4-.37-2.14 0-.74.13-1.46.37-2.14V6.59H1.86C1.3 7.98 1 9.46 1 11c0 1.54.3 3.02.86 4.41l3.77-2.95z"/><path fill="#EA4335" d="M12 4.75c1.67 0 3.17.58 4.35 1.72l3.26-3.26C17.63 1.4 15.06.5 12 .5 7.56.5 3.72 3.1 1.86 6.59l3.77 2.95c.9-2.7 3.41-4.79 6.37-4.79z"/></svg>
                Continue with Google
              </a>
              <div className="divider">OR</div>
            </>
          )}

          {modalMode === "signup" && (
            <input placeholder="First name" value={formName} onChange={(e)=>setFormName(e.target.value)} data-testid="auth-name" />
          )}
          <input placeholder="Email" type="email" value={formEmail} onChange={(e)=>setFormEmail(e.target.value)} data-testid="auth-email" />
          {modalMode !== "forgot" && (
            <input placeholder="Password" type="password" value={formPassword} onChange={(e)=>setFormPassword(e.target.value)} data-testid="auth-password" />
          )}
          {modalMode === "signup" && (
            <input placeholder="Confirm password" type="password" value={formConfirm} onChange={(e)=>setFormConfirm(e.target.value)} data-testid="auth-confirm" />
          )}

          {formError && (
            <div data-testid="auth-error" style={{color:"#f87171",fontSize:12,margin:"8px 0 0",textAlign:"center"}}>{formError}</div>
          )}
          {formInfo && (
            <div data-testid="auth-info" style={{color:"#A7F3D0",fontSize:12,margin:"8px 0 0",textAlign:"center"}}>{formInfo}</div>
          )}

          <button
            className="create"
            disabled={formBusy}
            data-testid={modalMode === "signin" ? "modal-signin-submit" : (modalMode === "forgot" ? "modal-forgot-submit" : "modal-create")}
            onClick={async () => {
              setFormError(""); setFormInfo("");
              const email = (formEmail || "").trim();
              const pwd = formPassword || "";
              if (!email) { setFormError("Please enter your email."); return; }
              if (modalMode !== "forgot" && pwd.length < 8) {
                setFormError("Password must be at least 8 characters.");
                return;
              }
              if (modalMode === "signup" && pwd !== formConfirm) {
                setFormError("Passwords don't match.");
                return;
              }
              setFormBusy(true);
              try {
                if (modalMode === "signup") {
                  await api.post("/auth/signup", { email, password: pwd, name: formName.trim() || undefined });
                  window.location.href = APP_URL;
                } else if (modalMode === "signin") {
                  await api.post("/auth/login", { email, password: pwd });
                  window.location.href = APP_URL;
                } else {
                  await api.post("/auth/forgot-password", { email });
                  setFormInfo("If this email is registered, a reset link is on its way.");
                }
              } catch (err) {
                const d = err?.response?.data?.detail;
                let msg;
                if (typeof d === "string") msg = d;
                else if (d && typeof d.message === "string") msg = d.message;
                else if (Array.isArray(d)) msg = d.map(e => e.msg || "").filter(Boolean).join(" ");
                else msg = err?.message || "Something went wrong. Please try again.";
                setFormError(msg);
              } finally {
                setFormBusy(false);
              }
            }}
          >
            {formBusy ? "Please wait…" :
              modalMode === "signin" ? "Sign in" :
              modalMode === "forgot" ? "Send reset link" : "Create account"}
          </button>

          <div className="signin-link" style={{marginTop:12}}>
            {modalMode === "signup" ? (
              <>Already have an account? <a href="#" onClick={(e)=>{e.preventDefault(); setModalMode("signin"); setFormError(""); setFormInfo("");}} data-testid="modal-switch-signin">Sign in</a></>
            ) : modalMode === "signin" ? (
              <>
                New here? <a href="#" onClick={(e)=>{e.preventDefault(); setModalMode("signup"); setFormError(""); setFormInfo("");}} data-testid="modal-switch-signup">Create account</a>
                {" · "}
                <a href="#" onClick={(e)=>{e.preventDefault(); setModalMode("forgot"); setFormError(""); setFormInfo("");}} data-testid="modal-switch-forgot">Forgot password?</a>
              </>
            ) : (
              <a href="#" onClick={(e)=>{e.preventDefault(); setModalMode("signin"); setFormError(""); setFormInfo("");}} data-testid="modal-switch-back">Back to sign in</a>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
